import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { plural } from '../lib/format.js'
import { display } from '../lib/score.js'

/**
 * One honest picture of a discography through the rater's own numbers
 * (1B-2 Q4/Q5): a dot per rated album, release year across, score up, a
 * line walking the dots in release order.
 *
 * Hand-drawn SVG rather than a charting dependency — a dozen dots, three
 * gridlines and one polyline do not justify one, and degrading gracefully at
 * a single album (one centered dot, no line) is easier to see here than to
 * configure.
 *
 * Hover answers with a real tooltip — album, score, when it was rated — as
 * an HTML overlay rather than SVG text, so it sizes to its words. Focus
 * answers the same way, because each dot is a link to its album (Q16). The
 * dots used to be kept out of the Tab order — a focusable node with no widget
 * role fails the axe gate's focus-order-semantics — which left keyboard,
 * screen-reader and touch users the summary and nothing else. A link is a
 * widget role, so the rule that kept them out is the rule that lets them in;
 * the svg is a labelled group rather than one image, and the axis marks are
 * hidden from it as the decoration they are.
 *
 * Descriptive, never competitive: only the viewer's scores, no community
 * overlay, no ranks, no averages-of-averages (PLAN §Risks). The partial-
 * catalog honesty comes free — only materialized albums can appear here, and
 * the page already says the discography is what Sonologue has fetched.
 */
const WIDTH = 640
const HEIGHT = 200
const PAD = { top: 16, right: 24, bottom: 28, left: 34 }

export default function DiscographyGraph({ dots }) {
  const [active, setActive] = useState(null)
  const navigate = useNavigate()
  const plottable = dots
    .filter((dot) => dot.year != null)
    .sort((a, b) => a.year - b.year || a.title.localeCompare(b.title))
  if (!plottable.length) return null

  const years = plottable.map((dot) => dot.year)
  const minYear = Math.min(...years)
  const maxYear = Math.max(...years)
  const span = Math.max(1, maxYear - minYear)
  const innerWidth = WIDTH - PAD.left - PAD.right
  const innerHeight = HEIGHT - PAD.top - PAD.bottom

  const x = (year) =>
    maxYear === minYear
      ? PAD.left + innerWidth / 2
      : PAD.left + ((year - minYear) / span) * innerWidth
  const y = (score) => PAD.top + (1 - score / 100) * innerHeight

  const points = plottable.map((dot) => ({ ...dot, cx: x(dot.year), cy: y(dot.score) }))
  const tip = points.find((point) => point.public_id === active) || null

  const summary =
    maxYear === minYear
      ? `Your scores for ${plottable.length} album${plottable.length === 1 ? '' : 's'} from ${minYear}`
      : `Your scores for ${plottable.length} ${plural(plottable.length, 'album')}, ${minYear} to ${maxYear}`

  return (
    <div className="disco-wrap">
      <svg
        className="disco-graph"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="group"
        aria-label={summary}
      >
        {[0, 50, 100].map((line) => (
          <g key={line} aria-hidden="true">
            <line
              className="disco-grid"
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(line)}
              y2={y(line)}
            />
            <text className="disco-tick num" x={PAD.left - 8} y={y(line) + 4} textAnchor="end">
              {line / 10}
            </text>
          </g>
        ))}
        <text
          className="disco-tick num"
          x={x(minYear)}
          y={HEIGHT - 8}
          textAnchor={maxYear === minYear ? 'middle' : 'start'}
          aria-hidden="true"
        >
          {minYear}
        </text>
        {maxYear !== minYear ? (
          <text
            className="disco-tick num"
            x={x(maxYear)}
            y={HEIGHT - 8}
            textAnchor="end"
            aria-hidden="true"
          >
            {maxYear}
          </text>
        ) : null}
        {points.length > 1 ? (
          <polyline
            className="disco-line"
            points={points.map((point) => `${point.cx},${point.cy}`).join(' ')}
          />
        ) : null}
        {points.map((point) => {
          const path = `/album/${point.public_id}`
          return (
            // W7 (Josh, 2026-09-24). The client routes it like any link; a
            // modified click keeps the browser's own meaning.
            <a
              key={point.public_id}
              href={path}
              aria-label={`${point.title}, ${point.year}: your score ${display(point.score)}`}
              onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return
                event.preventDefault()
                navigate(path)
              }}
              onMouseEnter={() => setActive(point.public_id)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(point.public_id)}
              onBlur={() => setActive(null)}
            >
              <circle
                className="disco-dot"
                cx={point.cx}
                cy={point.cy}
                r={active === point.public_id ? 6 : 5}
              />
            </a>
          )
        })}
      </svg>
      {tip ? (
        <div
          className={`disco-tip${tip.cy < HEIGHT / 2 ? ' below' : ''}`}
          style={{ left: `${(tip.cx / WIDTH) * 100}%`, top: `${(tip.cy / HEIGHT) * 100}%` }}
        >
          <strong>{tip.title}</strong>
          <span>
            {display(tip.score)}
            {tip.rated_at ? ` · rated ${new Date(tip.rated_at).toLocaleDateString()}` : ''}
          </span>
        </div>
      ) : null}
    </div>
  )
}
